import React from "react";
import styled from "styled-components";
import { keyframes } from "styled-components";
import { Button } from "../../../styles/components";
import {
  FaUserGraduate,
  FaBuilding,
  FaFileAlt,
  FaCog,
  FaChartLine,
  FaPhone,
  FaCalendarAlt,
} from "react-icons/fa";

// Advanced moving neon glow animations
const neonPulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.8; }
  100% { opacity: 0.3; }
`;

// Animation for the moving light border
const borderGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), inset 0 0 8px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
`;

// Orbital neon movement for the glow effect
const orbitGlow = keyframes`
  0% { transform: translateX(-100%) translateY(-100%) scale(0.8); }
  25% { transform: translateX(200%) translateY(-50%) scale(1); }
  50% { transform: translateX(200%) translateY(200%) scale(1.2); }
  75% { transform: translateX(-50%) translateY(200%) scale(1); }
  100% { transform: translateX(-100%) translateY(-100%) scale(0.8); }
`;

// Subtle perimeter scan effect
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
`;

const NavContainer = styled.nav`
  display: flex;
  gap: 1.25rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 0.85rem 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  /* Futuristic glassy border effect */
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  animation: ${borderGlow} 4s infinite ease-in-out;

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 15% 50%,
        rgba(99, 102, 241, 0.12) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 85% 30%,
        rgba(124, 58, 237, 0.08) 0%,
        transparent 45%
      );
    z-index: -1;
  }

  /* Dynamic neon glow orb that moves around container edges */
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;

    /* Star-like dots in background */
    background-image: radial-gradient(
        1px 1px at 20% 30%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 40% 70%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 60% 40%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 80% 60%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      );
    opacity: 0.4;
  }

  /* Moving perimeter glow effect */
  &:before {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(99, 102, 241, 0.1) 25%,
      rgba(124, 58, 237, 0.2) 50%,
      rgba(99, 102, 241, 0.1) 75%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${scanEffect} 8s linear infinite;
  }

  /* Orbital neon glow */
  .neon-orb {
    position: absolute;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: radial-gradient(
      circle at center,
      rgba(99, 102, 241, 0.4) 0%,
      rgba(124, 58, 237, 0.2) 40%,
      transparent 70%
    );
    filter: blur(20px);
    animation: ${orbitGlow} 15s infinite ease-in-out;
    z-index: -1;
    opacity: 0.6;
  }

  /* Pulsing neon spots */
  .neon-spot-1,
  .neon-spot-2,
  .neon-spot-3 {
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    z-index: -1;
    filter: blur(10px);
  }

  .neon-spot-1 {
    background: rgba(99, 102, 241, 0.7);
    top: 10px;
    right: 30%;
    animation: ${neonPulse} 3s infinite ease-in-out;
  }

  .neon-spot-2 {
    background: rgba(124, 58, 237, 0.7);
    bottom: 15px;
    left: 25%;
    animation: ${neonPulse} 4s 0.5s infinite ease-in-out;
  }

  .neon-spot-3 {
    background: rgba(79, 70, 229, 0.7);
    top: 50%;
    left: 15px;
    animation: ${neonPulse} 5s 1s infinite ease-in-out;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
    gap: 0.85rem;
  }
`;

const NavButton = styled(Button)`
  flex: 1;
  min-width: 150px;
  background: ${(props) =>
    props.active ? "rgba(79, 70, 229, 0.75)" : "rgba(15, 23, 42, 0.4)"};
  color: ${(props) => (props.active ? "white" : "rgba(255, 255, 255, 0.8)")};
  border: ${(props) =>
    props.active
      ? "1px solid rgba(99, 102, 241, 0.5)"
      : "1px solid rgba(148, 163, 184, 0.2)"};
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  /* Improved hover effect with neon glow */
  &:hover {
    background: ${(props) =>
      props.active ? "rgba(79, 70, 229, 0.85)" : "rgba(99, 102, 241, 0.25)"};
    transform: translateY(-3px);
    box-shadow: ${(props) =>
      props.active
        ? "0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 15px rgba(99, 102, 241, 0.6)"
        : "0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 12px rgba(99, 102, 241, 0.4)"};
    color: white;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.8);

    svg {
      transform: scale(1.1);
      filter: drop-shadow(0 0 5px rgba(99, 102, 241, 0.8));
    }
  }

  /* Active state enhanced neon glow effect */
  ${(props) =>
    props.active &&
    `
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
    box-shadow: 0 0 18px rgba(99, 102, 241, 0.6), inset 0 0 8px rgba(99, 102, 241, 0.4);
  `}

  /* Futuristic gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${(props) =>
        props.active
          ? "rgba(129, 140, 248, 0.4) 0%, rgba(79, 70, 229, 0.4) 100%"
          : "rgba(255, 255, 255, 0.05) 0%, rgba(15, 23, 42, 0.05) 100%"}
    );
    z-index: -1;
  }

  /* Enhanced shiny animation on hover */
  &:after {
    content: "";
    position: absolute;
    width: 150%;
    height: 200%;
    top: -200%;
    left: -50%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: rotate(25deg);
    transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    z-index: 1;
  }

  &:hover:after {
    top: 200%;
    left: 100%;
  }

  svg {
    font-size: 1.2rem;
    margin-right: 0.5rem;
    vertical-align: middle;
    position: relative;
    top: -1px;
    transition: all 0.3s ease;

    /* Enhanced icon glow effect on active state */
    ${(props) =>
      props.active &&
      `
      filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.7));
    `}
  }
`;

const DashboardNav = ({ activeView, onChangeView }) => {
  return (
    <NavContainer>
      {/* Dynamic neon glow effects */}
      <div className="neon-orb"></div>
      <div className="neon-spot-1"></div>
      <div className="neon-spot-2"></div>
      <div className="neon-spot-3"></div>
      <NavButton
        active={activeView === "dashboard"}
        onClick={() => onChangeView("dashboard")}
      >
        <FaChartLine /> Dashboard
      </NavButton>
      <NavButton
        active={activeView === "students"}
        onClick={() => onChangeView("students")}
      >
        <FaUserGraduate /> Students
      </NavButton>
      <NavButton
        active={activeView === "reports"}
        onClick={() => onChangeView("reports")}
      >
        <FaFileAlt /> Reports
      </NavButton>
      <NavButton
        active={activeView === "companies"}
        onClick={() => onChangeView("companies")}
      >
        <FaBuilding /> Companies
      </NavButton>
      <NavButton
        active={activeView === "workshops"}
        onClick={() => onChangeView("workshops")}
      >
        <FaCalendarAlt /> Workshops
      </NavButton>{" "}
      <NavButton
        active={activeView === "calls"}
        onClick={() => {
          // Clear any existing timers before switching to the appointments tab
          sessionStorage.removeItem("appointmentNotificationShown");
          onChangeView("calls");
        }}
      >
        <FaPhone /> Appointments
      </NavButton>
      <NavButton
        active={activeView === "settings"}
        onClick={() => onChangeView("settings")}
      >
        <FaCog /> Settings
      </NavButton>
    </NavContainer>
  );
};

export default DashboardNav;
